/* eslint-disable no-useless-escape */
import { Expression } from "@entaconsulting/form";
import Editor, { useMonaco } from "@monaco-editor/react";
import monaco, { editor } from "monaco-editor";
import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { setIsValid, updateExpression } from "../store/expressionEditorSlice";

const ExpressionEditor = () => {
  const dispatch = useAppDispatch();
  const monaco = useMonaco();
  const { expression } = useAppSelector(
    (state) => state.developer.edit.expressionEditor
  );
  const { schemaJsonEdited } = useAppSelector(
    (state) => state.developer.edit.editor
  );
  const monacoEditorRef = useRef<editor.IStandaloneCodeEditor>();
  const monacoRef = useRef<typeof monaco>();

  const runValidations = (expression: string) => {
    if (!monacoEditorRef.current || !monacoRef.current) return;

    const model = monacoEditorRef.current.getModel();
    if (!model) return;

    let errors: string[] = [];

    try {
      const expressionInstance = new Expression(expression);
      errors = expressionInstance.validate(JSON.parse(schemaJsonEdited));
    } catch (e) {
      if (e instanceof Error) {
        errors.push(e.message);
      }
    }

    let markers: monaco.editor.IMarkerData[] = [];

    if (errors.length !== 0) {
      markers = errors.map((errorData) => {
        return {
          severity: 8,
          startLineNumber: 1,
          startColumn: 20,
          endLineNumber: model.getLineCount(),
          endColumn: 1000000, // Long enough to reach the end of the line
          message: errorData,
        };
      });
      monacoRef.current.editor.setModelMarkers(model, "mathjs", markers);
      dispatch(setIsValid(false));
      return;
    }
    monacoRef.current.editor.setModelMarkers(model, "mathjs", markers);
    dispatch(setIsValid(true));
  };

  // useEffect to avoid multi registering, source: https://github.com/suren-atoyan/monaco-react/issues/110
  useEffect(() => {
    if (monaco) {
      const mathjsFunctions = Expression.getAvailableFunctionsList();
      monaco.languages.register({ id: "mathjs" });

      monaco.languages.setLanguageConfiguration("mathjs", {
        surroundingPairs: [
          { open: "{", close: "}" },
          { open: "[", close: "]" },
          { open: "(", close: ")" },
        ],
        autoClosingPairs: [
          { open: "{", close: "}" },
          { open: "[", close: "]" },
          { open: "(", close: ")" },
        ],
        brackets: [
          ["{", "}"],
          ["(", ")"],
          ["[", "]"],
        ],
      });

      // Register a tokens provider for the language
      monaco.languages.setMonarchTokensProvider("mathjs", {
        // Set defaultToken to invalid to see what you do not tokenize yet
        // defaultToken: 'invalid',

        keywords: mathjsFunctions,
        digits: /\d+(_+\d+)*/,
        octaldigits: /[0-7]+(_+[0-7]+)*/,
        binarydigits: /[0-1]+(_+[0-1]+)*/,
        hexdigits: /[[0-9a-fA-F]+(_+[0-9a-fA-F]+)*/,
        brackets: [
          { open: "{", close: "}", token: "delimiter.curly" },
          { open: "[", close: "]", token: "delimiter.bracket" },
          { open: "(", close: ")", token: "delimiter.parenthesis" },
        ],
        operators: [
          "<=", //
          ">=", //
          "==", //
          "!=", //
          "+", //
          "-", //
          "*", //
          ".*", //
          "/", //
          "./", //
          "%", //
          "<", //
          ">", //
          "<<", //
          "and", //
          "not", //
          "or", //
          "xor", //
          "to", //
          "in", //
          ">>", //
          ">>>", //
          "&", //
          "|", //
          "^", //
          ".^", //
          "'", //
          "!", //
          "~", //
          "^|", //
          "?", //
          ":", //
          "=", //
        ],
        // The main tokenizer for our languages
        tokenizer: {
          root: [
            // identifiers and keywords
            [
              /[a-zA-Z_$][\w$]*/,
              {
                cases: {
                  // "@typeKeywords": "keyword",
                  "@keywords": "keyword",
                  "@default": "identifier",
                },
              },
            ],
            // whitespace
            { include: "@whitespace" },

            // numbers
            [/(@digits)[eE]([\-+]?(@digits))?/, "number.float"],
            [/(@digits)\.(@digits)([eE][\-+]?(@digits))?/, "number.float"],
            [/0[xX](@hexdigits)/, "number.hex"],
            [/0[oO]?(@octaldigits)/, "number.octal"],
            [/0[bB](@binarydigits)/, "number.binary"],
            [/(@digits)/, "number"],
          ],

          whitespace: [
            [/[ \t\r\n]+/, "white"],
            [/#.*$/, "comment"],
          ],
        },
      });
    }
  }, [monaco]);

  const options: monaco.editor.IStandaloneEditorConstructionOptions = {
    minimap: { enabled: false },
    bracketPairColorization: { enabled: true },
    matchBrackets: "near",
    wordWrap: "on",
  };

  return (
    <Editor
      defaultLanguage="mathjs"
      value={expression}
      onChange={(value) => {
        const newExpression = value || "";
        dispatch(updateExpression(newExpression));
        runValidations(newExpression);
      }}
      onMount={(editor, monaco) => {
        monacoEditorRef.current = editor;
        monacoRef.current = monaco;
        runValidations(expression || "");
      }}
      options={options}
    />
  );
};

export default ExpressionEditor;
